/**
 * O recorte manual na aba Documentos (E214.F1, GUS-314): "Recortar" na linha do documento abre este
 * diálogo. O perito escolhe a página, desenha a área e diz o que ela é — documento de identidade
 * (frente ou verso), selfie da formalização ou comprovante de TED — e salva.
 *
 * O servidor recorta a própria página e grava um derivado; o original não muda. A frente e a selfie
 * entram na comparação facial, que é refeita; o verso é lido e a leitura aparece na linha para o
 * perito confirmar; o comprovante é lido na hora, sem digitação, e o formato que o leitor não
 * entende fica recebido e não lido. A tela diz qual das quatro coisas aconteceu.
 */
import type { AxiosInstance } from 'axios';
import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Radio,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { NormalizedBoundingBox } from '@/lib/bbox_coords';
import {
  DESTINOS_DO_RECORTE,
  recortarDocumento,
  RecorteDoDocumentoError,
  resultadoDoRecorte,
  type DestinoDoRecorte,
  type MotivoDaRecusaDoRecorte,
  type RecorteFeitoData,
} from '@/lib/recorte_do_documento';

import { PaginaDoRecorte } from './PaginaDoRecorte';

const t = ptBR.recorteDoDocumento;

export interface DocumentoARecortar {
  id: string;
  nome: string;
  /** O total de páginas, quando se sabe (a imagem solta tem uma). */
  paginas: number | null;
}

export interface RecorteDoDocumentoProps {
  client: AxiosInstance;
  caseId: string;
  documento: DocumentoARecortar;
  onFechar: () => void;
  /** O recorte foi gravado: a mesa relê o caso (fotografias, leituras e crédito). */
  onRecortado: (feito: RecorteFeitoData) => void;
}

function Destinos({
  destino,
  onDestino,
}: {
  destino: DestinoDoRecorte | null;
  onDestino: (destino: DestinoDoRecorte) => void;
}): JSX.Element {
  return (
    <fieldset className="space-y-sc-1" data-testid="recorte-destinos">
      <legend className="text-body-sm font-medium text-fg">{t.oQueE}</legend>
      {DESTINOS_DO_RECORTE.map((d) => (
        <Radio
          key={d}
          name="destino-do-recorte"
          value={d}
          checked={destino === d}
          onChange={() => onDestino(d)}
          wrapperClassName="flex w-full items-start"
          data-testid={`recorte-destino-${d}`}
        >
          <span className="flex flex-col">
            <span>{t.destino[d]}</span>
            <span className="text-caption text-fg-muted">{t.ajudaDoDestino[d]}</span>
          </span>
        </Radio>
      ))}
    </fieldset>
  );
}

export function RecorteDoDocumento({
  client,
  caseId,
  documento,
  onFechar,
  onRecortado,
}: RecorteDoDocumentoProps): JSX.Element {
  const [pagina, setPagina] = useState(1);
  const [area, setArea] = useState<NormalizedBoundingBox | null>(null);
  const [destino, setDestino] = useState<DestinoDoRecorte | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [motivo, setMotivo] = useState<MotivoDaRecusaDoRecorte | null>(null);
  const [feito, setFeito] = useState<RecorteFeitoData | null>(null);

  const irPara = (n: number): void => {
    setPagina(n);
    setArea(null); // a área é da página: trocar de página pede outra
  };
  const falta = area === null ? t.faltaArea : destino === null ? t.faltaDestino : null;

  const salvar = async (): Promise<void> => {
    if (area === null || destino === null) return;
    setSalvando(true);
    setMotivo(null);
    try {
      const gravado = await recortarDocumento(client, caseId, {
        documento_id: documento.id,
        pagina,
        area,
        destino,
      });
      setFeito(gravado);
      onRecortado(gravado);
    } catch (err) {
      setMotivo(err instanceof RecorteDoDocumentoError ? err.motivo : 'falha');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(aberto) => (aberto ? undefined : onFechar())}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="recorte">
        <DialogHeader>
          <DialogTitle>{t.titulo}</DialogTitle>
          <DialogDescription>
            {documento.nome} · {t.descricao}
          </DialogDescription>
        </DialogHeader>
        {feito !== null ? (
          <p role="status" className="text-body-sm text-fg" data-testid="recorte-resultado">
            {t.resultado[resultadoDoRecorte(feito)]}
          </p>
        ) : (
          <div className="space-y-sc-3">
            <PaginaDoRecorte
              client={client}
              documentoId={documento.id}
              pagina={pagina}
              total={documento.paginas}
              area={area}
              onPagina={irPara}
              onArea={setArea}
            />
            <Destinos destino={destino} onDestino={setDestino} />
            {motivo !== null ? (
              <p role="alert" className="text-body-sm text-danger-700" data-testid="recorte-erro">
                {t.porQue[motivo]}
              </p>
            ) : null}
          </div>
        )}
        <DialogFooter>
          {feito !== null ? (
            <Button type="button" onClick={onFechar} data-testid="recorte-fechar">
              {t.fechar}
            </Button>
          ) : (
            <>
              {falta !== null ? (
                <span className="mr-auto text-caption text-fg-muted" data-testid="recorte-falta">
                  {falta}
                </span>
              ) : null}
              <Button type="button" variant="ghost" onClick={onFechar}>
                {t.cancelar}
              </Button>
              <Button
                type="button"
                disabled={falta !== null || salvando}
                onClick={() => void salvar()}
                data-testid="recorte-salvar"
              >
                {salvando ? t.salvando : t.salvar}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
